/**
 * Swiper — sliders nommés du thème
 *
 * Chaque slider est déclaré dans SLIDER_REGISTRY.
 * Le markup doit inclure data-slider="{name}" et la structure Swiper (.swiper-wrapper / .swiper-slide).
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

Swiper.use([Navigation, Pagination, Autoplay]);

/** @type {Map<HTMLElement, Swiper>} */
const instances = new Map();

/**
 * Nombre de slides visibles pour la largeur courante (base + breakpoints).
 *
 * @param {import('swiper').SwiperOptions} options
 * @param {number} [width]
 * @returns {number}
 */
function getSlidesPerViewForWidth(options, width = window.innerWidth) {
    let slidesPerView = typeof options.slidesPerView === 'number' ? options.slidesPerView : 1;

    if (options.breakpoints) {
        Object.entries(options.breakpoints)
            .map(([point, breakpoint]) => [Number(point), breakpoint])
            .sort(([a], [b]) => a - b)
            .forEach(([point, breakpoint]) => {
                if (width >= point && typeof breakpoint?.slidesPerView === 'number') {
                    slidesPerView = breakpoint.slidesPerView;
                }
            });
    }

    return slidesPerView;
}

/**
 * @param {number} slideCount
 * @param {import('swiper').SwiperOptions} options
 * @param {number} [width]
 * @returns {boolean}
 */
function needsNavigation(slideCount, options, width = window.innerWidth) {
    return slideCount > getSlidesPerViewForWidth(options, width);
}

/**
 * Affiche ou masque les flèches selon le débordement réel du carrousel.
 *
 * @param {import('swiper').Swiper} swiper
 * @param {number} slideCount
 * @param {HTMLElement|null} nextEl
 * @param {HTMLElement|null} prevEl
 */
function syncNavigationVisibility(swiper, slideCount, nextEl, prevEl) {
    const show = needsNavigation(slideCount, swiper.params, swiper.width);

    [nextEl, prevEl].forEach((el) => {
        if (el instanceof HTMLElement) {
            el.hidden = !show;
        }
    });

    if (!show && swiper.params.loop) {
        swiper.loopDestroy();
        swiper.params.loop = false;
    }
}

/**
 * Registre des sliders.
 * @type {Record<string, { selector: string, options: import('swiper').SwiperOptions }>}
 */
export const SLIDER_REGISTRY = {
    'home-acces-rapides': {
        selector: '[data-slider="home-acces-rapides"]',
        options: {
            slidesPerView: 2,
            spaceBetween: 8,
            centeredSlides: false,
            watchOverflow: true,
            roundLengths: true,
            loop: true,
            autoplay: {
                delay: 3000,
                speed: 1000,
                disableOnInteraction: true,
            },
            pagination: {
                enabled: false,
            },
            navigation: {
                enabled: true,
            },
            breakpoints: {
                768: {
                    slidesPerView: 3,
                    spaceBetween: 8,
                },
                991: {
                    slidesPerView: 4,
                    spaceBetween: 8,
                    allowTouchMove: false,
                },
            },
        },
    },
    'partenaires-footer-swiper': {
        selector: '[data-slider="partenaires-footer-swiper"]',
        options: {
            slidesPerView: 3,
            spaceBetween: 24,
            loop: true,
            autoplay: {
                delay: 3000,
                speed: 1000,
                disableOnInteraction: true,
            },
            breakpoints: {
                768: {
                    slidesPerView: 4,
                    spaceBetween: 36,
                },
                991: {
                    slidesPerView: 5,
                },
            },
        },
    },
};

/**
 * Initialise un ou plusieurs sliders enregistrés.
 *
 * @param {string[]} [names] Noms des sliders (tous si omis).
 */
export function initSliders(names = null) {
    const entries = Object.entries(SLIDER_REGISTRY).filter(
        ([name]) => !names || names.includes(name)
    );

    entries.forEach(([name, { selector, options }]) => {
        document.querySelectorAll(selector).forEach((element) => {
            if (!(element instanceof HTMLElement)) {
                return;
            }

            if (instances.has(element) || element.dataset.swiperInitialized === 'true') {
                return;
            }

            const paginationEl = element.querySelector('.swiper-pagination');
            const nextEl = element.querySelector('.swiper-button-next');
            const prevEl = element.querySelector('.swiper-button-prev');

            /** @type {import('swiper').SwiperOptions} */
            const swiperOptions = { ...options };

            const paginationConfig = swiperOptions.pagination;
            const paginationEnabled =
                paginationConfig !== false &&
                paginationConfig !== undefined &&
                (typeof paginationConfig !== 'object' || paginationConfig.enabled !== false);

            if (paginationEl && paginationEnabled) {
                swiperOptions.pagination = {
                    ...(typeof paginationConfig === 'object' ? paginationConfig : {}),
                    el: paginationEl,
                    clickable: true,
                };
            }

            if (nextEl && prevEl) {
                swiperOptions.navigation = {
                    ...(typeof swiperOptions.navigation === 'object'
                        ? swiperOptions.navigation
                        : {}),
                    nextEl,
                    prevEl,
                };
            }

            const slideCount = element.querySelectorAll('.swiper-slide').length;

            if (swiperOptions.loop && !needsNavigation(slideCount, swiperOptions)) {
                swiperOptions.loop = false;
            }

            if (nextEl && prevEl) {
                const existingOn = swiperOptions.on ?? {};

                swiperOptions.on = {
                    ...existingOn,
                    init(swiper) {
                        existingOn.init?.(swiper);
                        syncNavigationVisibility(swiper, slideCount, nextEl, prevEl);
                    },
                    breakpoint(swiper) {
                        existingOn.breakpoint?.(swiper);
                        syncNavigationVisibility(swiper, slideCount, nextEl, prevEl);
                    },
                    resize(swiper) {
                        existingOn.resize?.(swiper);
                        syncNavigationVisibility(swiper, slideCount, nextEl, prevEl);
                    },
                };
            }

            const swiper = new Swiper(element, swiperOptions);

            element.dataset.swiperInitialized = 'true';
            instances.set(element, swiper);
        });
    });
}

/**
 * Raccourci : initialise tous les sliders enregistrés.
 */
export function initAllSliders() {
    initSliders();
}
